import type {
    CancelInvoiceDTO,
    CancelInvoiceResult,
    CfdiStatus,
    CreateInvoiceDTO,
    ICfdiRepository,
    Invoice,
    ValidateRfcResult,
} from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiCfdiStatusResponse {
  provider: string;
  isConfigured: boolean;
}

interface ApiValidateRfcResponse {
  rfc: string;
  isValid: boolean;
}

interface ApiInvoiceResponse {
  uuid: string;
  series: string;
  folio: string;
  total: number;
  subtotal: number;
  xml: string;
  pdf: string;
  qrCode: string;
  certificationDate: string;
  satCertNumber: string;
  status: 'active' | 'cancelled';
}

interface ApiCancelInvoiceResponse {
  uuid: string;
  status: 'cancelled' | 'pending' | 'rejected';
  acknowledgement: string;
}

// Mapea la respuesta de la API al tipo CfdiStatus interno
const mapApiStatusToStatus = (apiStatus: ApiCfdiStatusResponse): CfdiStatus => ({
  provider: apiStatus.provider,
  isConfigured: apiStatus.isConfigured,
});

// Mapea la respuesta de la API al tipo ValidateRfcResult interno
const mapApiValidateRfcToResult = (apiResult: ApiValidateRfcResponse): ValidateRfcResult => ({
  rfc: apiResult.rfc,
  isValid: apiResult.isValid,
});

// Mapea la respuesta de la API al tipo Invoice interno
const mapApiInvoiceToInvoice = (apiInvoice: ApiInvoiceResponse): Invoice => ({
  uuid: apiInvoice.uuid,
  series: apiInvoice.series,
  folio: apiInvoice.folio,
  total: apiInvoice.total,
  subtotal: apiInvoice.subtotal,
  xml: apiInvoice.xml,
  pdf: apiInvoice.pdf,
  qrCode: apiInvoice.qrCode,
  certificationDate: apiInvoice.certificationDate,
  satCertNumber: apiInvoice.satCertNumber,
  status: apiInvoice.status,
});

// Mapea la respuesta de la API al tipo CancelInvoiceResult interno
const mapApiCancelToResult = (apiCancel: ApiCancelInvoiceResponse): CancelInvoiceResult => ({
  uuid: apiCancel.uuid,
  status: apiCancel.status,
  acknowledgement: apiCancel.acknowledgement,
});

export class ApiCfdiRepository implements ICfdiRepository {
  async getStatus(): Promise<CfdiStatus> {
    const client = getApiClient();
    const response = await client.get<ApiCfdiStatusResponse>('v1/payments/cfdi/status');
    return mapApiStatusToStatus(response);
  }

  async validateRfc(rfc: string): Promise<ValidateRfcResult> {
    const client = getApiClient();
    const response = await client.post<ApiValidateRfcResponse>('v1/payments/cfdi/validate-rfc', { rfc });
    return mapApiValidateRfcToResult(response);
  }

  async createInvoice(invoice: CreateInvoiceDTO): Promise<Invoice> {
    const client = getApiClient();
    const apiInvoice = await client.post<ApiInvoiceResponse>('v1/payments/cfdi/invoice', invoice);
    return mapApiInvoiceToInvoice(apiInvoice);
  }

  async getInvoice(uuid: string): Promise<Invoice> {
    const client = getApiClient();
    const apiInvoice = await client.get<ApiInvoiceResponse>(`v1/payments/cfdi/invoice/${uuid}`);
    return mapApiInvoiceToInvoice(apiInvoice);
  }

  async cancelInvoice(uuid: string, cancel: CancelInvoiceDTO): Promise<CancelInvoiceResult> {
    const client = getApiClient();
    // Usar DELETE con body a través de options
    const apiCancel = await client.delete<ApiCancelInvoiceResponse>(
      `v1/payments/cfdi/invoice/${uuid}`,
      { body: cancel }
    );
    return mapApiCancelToResult(apiCancel);
  }

  async downloadPdf(uuid: string): Promise<Blob> {
    const client = getApiClient();
    return await client.get<Blob>(`v1/payments/cfdi/invoice/${uuid}/pdf`, {
      responseType: 'blob',
    });
  }

  async downloadXml(uuid: string): Promise<Blob> {
    const client = getApiClient();
    return await client.get<Blob>(`v1/payments/cfdi/invoice/${uuid}/xml`, {
      responseType: 'blob',
    });
  }
}

